import type { Observable} from 'rxjs';
import { forkJoin, of } from 'rxjs';
import { defaultIfEmpty, map, mergeMap } from 'rxjs/operators';
import { mapValues, isEmpty, omitBy, isEqualWith, range, pickBy } from 'lodash-es';

import type {
    SettingValueMap,
    SettingDefinitionMap,
    SettingValueMapDiff} from 'portlet/models/settings';
import {
    SettingKeys,
    Settings,
    SettingDefinition
} from 'portlet/models/settings';
import type {
    InternalKeySettingDefinition,
    HierarchyIdSettingDefinition,
    HierarchyVariantIdSettingDefinition,
    DimensionValuesSettingDefinition,
    SmartViewValuesSettingDefinition,
    LinkedHierarchiesSettingDefinition,
    HierarchyValuesSettingDefinition,
} from 'portlet/models/settings/settingDefinitions';
import { Specification } from 'portlet/models/specification';
import type { CacheService } from 'portlet/common/services';
import { View } from 'portlet/models/view';
import { getAggregationsFromSpec } from 'portlet/models/settings/settingDefinitions/smartViewSetting';
import type { FilterData, FilterIdToSignalMapping } from 'portlet/models';
import type { SettingFormInputDefinition } from 'header/types';
import { CustomSettingFormDefinitionInput } from 'portlet/models/settings/customSettingFormDefinitionInput';

import type { MasterDataService } from './MasterdataService';

/**
 * Properties required for reloading setting definitions (either caused by the fact definition requires async calls),
 * or by the fact it changes in accordance to other settings.
 */
type LoadDefinitionProps = {
    /** Current setting definitions */
    definitions: Partial<SettingDefinitionMap>;
    /** Current setting values */
    settings: Partial<SettingValueMap>;
    /** merged `setting` fields from spec main config and current view */
    specSettings: Partial<Settings>;
    /** changes between current and previous settings */
    diff: SettingValueMapDiff;
    specification: Specification;
    currentView: View | null;
    filters: FilterData[];
};

/**
 * Represents hierarchy of setting values coming from different sources and having different priorities.
 * Starting from least prioritized:
 * 1. `defaultSettings` - static values loaded only once on initialization
 * 2. `computedDefaultSettings` - dynamic values that apply if no higher precedence settings apply (currently obtained from selected portlet views)
 * 3. `settings` - settings explicitly set by the user via interaction,
 *      or by applying setting dependency (see settingDefinitions/_common.ts#applySettingDependency),
 *      or by applying a filter (applies only to filterable settings)
 *
 * When updating settings, following rules apply:
 * 1. Change of specific known setting in `computedDefaultSettings` **unsets** value at `settings`,
 *  as long as it was not set at the same time (see SettingService.unsetOnChange).
 * 2. Change of setting at any level may case setting dependencies to be recalculated
 */
export type SettingStack = {
    defaultSettings: Partial<SettingValueMap>;
    computedDefaultSettings: Partial<SettingValueMap>;
    settings: Partial<SettingValueMap>;
};

export const SettingStack = {
    merge: (stackA: SettingStack, stackB: SettingStack): SettingStack => ({
        defaultSettings: Settings.merge(stackA.defaultSettings, stackB.defaultSettings),
        computedDefaultSettings: Settings.merge(
            stackA.computedDefaultSettings,
            stackB.computedDefaultSettings,
        ),
        settings: Settings.merge(stackA.settings, stackB.settings),
    }),
};

/**
 * Handles
 * a) changing settings based on changes in other setting values
 *      e.g. unset selected hierarchy id when internal key changes
 * b) fetching async settings (e.g. settings based on masterdata)
 */
export class SettingService {
    constructor(
        private readonly masterData: MasterDataService,
        private readonly cache: CacheService,
    ) {}

    public applySettingDependenciesAndReloadDefinitions(
        previousStack: SettingStack,
        stack: SettingStack,
        specification: Specification,
        definitions: Partial<SettingDefinitionMap>,
        filters: FilterData[],
        listenToSignalMapping: FilterIdToSignalMapping,
    ): Observable<{ definitions: Partial<SettingDefinitionMap>; stack: SettingStack }> {
        const previousMerged = Settings.merge(
            previousStack.defaultSettings,
            previousStack.computedDefaultSettings,
            previousStack.settings,
        );
        const currentMerged = Settings.merge(
            stack.defaultSettings,
            stack.computedDefaultSettings,
            stack.settings,
        );
        const { defaultSettings, computedDefaultSettings, settings } = stack;

        const newComputedDefaultSettings = this.computeNewDefaultSettings(
            defaultSettings,
            computedDefaultSettings,
            settings,
            specification,
        );

        const didViewChange =
            currentMerged.VIEWID !== previousMerged.VIEWID &&
            // ignore scenario when this function runs first time the portlet was initialized
            previousMerged.VIEWID !== View.INVALID_VIEW_ID;

        // unset known user settings by explicit change caused by view
        const settingsSansViewChange = didViewChange
            ? SettingService.unsetOnChange(
                  settings,
                  previousStack.settings,
                  newComputedDefaultSettings,
                  previousStack.computedDefaultSettings,
              )
            : settings;

        // drop known setting values which are not applicable to current definitions (which could have changed due to view change)
        const settingsApplyingToNewDefinitions = pickBy(
            settingsSansViewChange,
            (_, key) => key in definitions || !SettingDefinition.isKnownSettingKey(key),
        );

        const merged = Settings.merge(
            defaultSettings,
            newComputedDefaultSettings,
            settingsApplyingToNewDefinitions,
        );

        const currentView = Specification.getViewById(
            specification,
            merged.VIEWID ?? specification.defaultViewId,
        );

        /**
         * This function recursively reloads setting definitions and updates dependant setting values.
         * In order to apply the deps, we need to first have definitions which are up to date,
         * but on the other hand applying the deps may require reloading specific setting definitions - therefore the recurrence.
         *
         * We stop the recurrence when no dependant setting changes value. If there are no setting dependency
         * cycles, this should not produce endless loops.
         */
        return this.loadDefinitions({
            definitions,
            settings: merged,
            specification,
            currentView,
            diff: Settings.diffSettings(merged, previousMerged),
            specSettings: Settings.mergeSpecSettings(specification.settings, currentView?.settings),
            filters,
        }).pipe(
            mergeMap(updatedDefinitions => {
                const updatedDependentSettings = SettingService.applySettingDependencies(
                    updatedDefinitions,
                    definitions,
                    merged,
                    previousMerged,
                    specification,
                    currentView,
                    filters,
                    listenToSignalMapping,
                );

                const settingsWithDepChanges = Settings.applyDiff(
                    settingsApplyingToNewDefinitions,
                    updatedDependentSettings,
                );

                const newStack: SettingStack = {
                    defaultSettings,
                    computedDefaultSettings: newComputedDefaultSettings,
                    settings: settingsWithDepChanges,
                };

                return isEmpty(updatedDependentSettings) &&
                    fuzzyArrayDeepEqual(definitions, updatedDefinitions)
                    ? of({ definitions: updatedDefinitions, stack: newStack })
                    : this.applySettingDependenciesAndReloadDefinitions(
                          stack,
                          newStack,
                          specification,
                          updatedDefinitions,
                          filters,
                          listenToSignalMapping,
                      );
            }),
        );
    }

    public static applySettingDependencies(
        definitions: Partial<SettingDefinitionMap>,
        previousDefinitions: Partial<SettingDefinitionMap>,
        newSettingValues: Partial<SettingValueMap>,
        oldSettingValues: Partial<SettingValueMap>,
        specification: Specification,
        currentView: View | null,
        filters: FilterData[],
        listenToSignalMapping: FilterIdToSignalMapping,
    ): SettingValueMapDiff {
        const diff = Settings.diffSettings(newSettingValues, oldSettingValues);

        return SettingDefinition.applyDependencies(
            specification,
            currentView,
            diff,
            newSettingValues,
            oldSettingValues,
            definitions,
            previousDefinitions,
            filters,
            listenToSignalMapping,
        );
    }

    public loadDefinitions(props: LoadDefinitionProps): Observable<Partial<SettingDefinitionMap>> {
        const definitions$ = mapValues(props.definitions, d => {
            return d && SettingDefinition.isKnownSettingDefinition(d)
                ? this.loadSettingDefinition(d, props)
                : d
                ? of(this.reloadCustomSettingDefinitionOnViewChange(d, props))
                : of(d);
        }) as { [key in keyof SettingDefinitionMap]: Observable<SettingDefinitionMap[key]> };

        return forkJoin(definitions$).pipe(defaultIfEmpty({}));
    }

    public static markLazySettingAsPending(setting: SettingDefinition): SettingDefinition {
        return setting.lazy === 'uninitialized' ? { ...setting, lazy: 'loading' } : setting;
    }

    private computeNewDefaultSettings(
        defaultSettings: Partial<SettingValueMap>,
        computedDefaultSettings: Partial<SettingValueMap>,
        settings: Partial<SettingValueMap>,
        specification: Specification,
    ): Partial<SettingValueMap> {
        const merged = Settings.merge(defaultSettings, computedDefaultSettings, settings);
        const view =
            merged.VIEWID !== undefined
                ? Specification.getViewById(specification, merged.VIEWID)
                : null;

        return SettingDefinition.toValueMap(specification, view, view?.settings || {});
    }

    private static unsetOnChange(
        currentA: Partial<SettingValueMap>,
        previousA: Partial<SettingValueMap>,
        currentB: Partial<SettingValueMap>,
        previousB: Partial<SettingValueMap>,
    ): Partial<SettingValueMap> {
        const diffA = Settings.diffSettings(currentA, previousA);
        const diffB = Settings.diffSettings(currentB, previousB);

        const knownSettings = omitBy(
            currentA,
            (_, key) =>
                // if it was set by B change
                diffB[key]?.current !== undefined &&
                // and was not set explicitly
                diffA[key]?.current === undefined,
        );

        // the inteded behavior for custom settings is different than for known settings,
        // we want to preserve user value over new computed default
        const unknownSettings = omitBy(currentA, (_, key) => {
            return SettingDefinition.isKnownSettingKey(key);
        });

        return {
            ...knownSettings,
            ...unknownSettings,
        };
    }

    private loadSettingDefinition(
        definition: SettingDefinition,
        props: LoadDefinitionProps,
    ): Observable<SettingDefinition> {
        const definitionAfterViewChange = this.reloadSettingDefinitionOnViewChange(
            definition,
            props,
        );

        // skip loading of setting definition for lazy settings
        if (definition.lazy === 'uninitialized') return of(definitionAfterViewChange);

        switch (definitionAfterViewChange.key) {
            case SettingKeys.INTERNAL_KEY:
                return this.loadInternalKeySetting(definitionAfterViewChange, props);
            case SettingKeys.HIERARCHY_ID:
                return this.loadHierarchySetting(definitionAfterViewChange, props);
            case SettingKeys.HIERARCHY_VARIANT_ID:
                return this.loadHierarchyVariantSetting(definitionAfterViewChange, props);
            case SettingKeys.LINKED_HIERARCHY_IDS:
                return this.loadContextHierarchySetting(definitionAfterViewChange, props);
            case SettingKeys.DIMENSION_VALUES:
                return this.loadDimensionValues(definitionAfterViewChange, props);
            case SettingKeys.HIERARCHY_VALUES:
                return this.loadHierarchyValues(definitionAfterViewChange, props);
            case SettingKeys.SMART_VIEW_VALUES:
                return this.loadSmartViewValues(definitionAfterViewChange, props);
            /**
             * settings which definitions should change after view change
             */
            default:
                return of(definitionAfterViewChange);
        }
    }

    /**
     * setting definitions may change after view changes, because on different views
     * settings may have different properties specified using the object notation
     *
     * also, some settings (DRILL, CONDITION) depend on view level properties (e.g. query)
     */
    private reloadSettingDefinitionOnViewChange(
        definition: SettingDefinition,
        props: LoadDefinitionProps,
    ): SettingDefinition {
        if (!props.diff.VIEWID) return definition;

        const newDefinition = SettingDefinition.createDefinitionFromSpec[definition.key](
            props.specSettings,
            props.specification,
            props.currentView,
        );

        /**
         * case when `newDefinition` would be `undefined` is currently impossible,
         * since `createDefinitionFromSpec` returns `undefined` only if the setting is not
         * declared on spec/view level. If it was not defined at all, then `loadDrillSetting`
         * would also never be triggered.
         */
        return newDefinition || definition;
    }

    /**
     * we can define custom setting with the same key on multiple views with diffrent properties,
     * in that case we also want to reload their definition
     */

    private reloadCustomSettingDefinitionOnViewChange(
        definition: SettingFormInputDefinition,
        props: LoadDefinitionProps,
    ): SettingFormInputDefinition {
        if (!props.diff.VIEWID) return definition;

        const definitionInput = props.currentView?.settings?.[definition.key];

        const newDefinition = CustomSettingFormDefinitionInput.codec.is(definitionInput)
            ? CustomSettingFormDefinitionInput.toSettingFormInputDefinition(
                  definition.key,
                  definitionInput,
              )
            : null;

        return newDefinition || definition;
    }

    private loadDimensionValues(
        definition: DimensionValuesSettingDefinition,
        { settings }: LoadDefinitionProps,
    ): Observable<DimensionValuesSettingDefinition> {
        const internalKey = settings.INTERNAL_KEY;
        if (!internalKey) return of(definition);
        const cacheKey = `loadDimensionValues-${internalKey}`;
        const options$ = this.cache.withCachedObservable(cacheKey, () =>
            this.masterData.getDimensionValues(internalKey).pipe(
                map(selectors => {
                    selectors.forEach(selector => {
                        selector.label = `${selector.value} [${selector.key}]`;
                    });

                    return selectors.sort((a, b) => a.label.localeCompare(b.label));
                }),
            ),
        );

        return options$.pipe(
            map(options =>
                SettingDefinition.createDefinition.DIMENSION_VALUES({
                    ...definition,
                    options,
                    lazy: 'initialized',
                }),
            ),
        );
    }

    private loadHierarchyValues(
        definition: HierarchyValuesSettingDefinition,
        { settings }: LoadDefinitionProps,
    ): Observable<HierarchyValuesSettingDefinition> {
        const hierarchyId = settings.HIERARCHY_ID;
        if (!hierarchyId) return of(definition);

        const cacheKey = `loadHierarchyValues-${hierarchyId}`;
        const hierarchies$ = this.cache.withCachedObservable(cacheKey, () =>
            this.masterData.getHierarchyById(hierarchyId),
        );

        return hierarchies$.pipe(
            map(options =>
                SettingDefinition.createDefinition.HIERARCHY_VALUES({
                    ...definition,
                    options: options,
                    lazy: 'initialized',
                }),
            ),
        );
    }

    private loadInternalKeySetting(
        definition: InternalKeySettingDefinition,
        _props: LoadDefinitionProps,
    ): Observable<InternalKeySettingDefinition> {
        return this.masterData.getDimensions().pipe(
            map(dimensions => {
                const options = dimensions.map(dimension => ({
                    ...dimension,
                    key: dimension.internalKey,
                    label: dimension.characteristicNameLong,
                }));

                return SettingDefinition.createDefinition.INTERNAL_KEY({ ...definition, options });
            }),
        );
    }

    private loadHierarchySetting(
        definition: HierarchyIdSettingDefinition,
        { settings }: LoadDefinitionProps,
    ): Observable<HierarchyIdSettingDefinition> {
        const internalKey = settings.INTERNAL_KEY;
        const nodes$ = internalKey
            ? this.masterData.getHierarchiesBasedOnInternalKey(internalKey)
            : SettingKeys.HIERARCHY_VARIANT_ID in settings
            ? this.masterData.getHierarchies(['variant'])
            : this.masterData.getHierarchies(['virtual', 'real']);

        return nodes$.pipe(
            map(hierarchies => {
                const options = hierarchies.map(hierarchy => ({
                    ...hierarchy,
                    key: hierarchy.hierarchyId,
                    label: hierarchy.hierarchyName,
                }));

                return SettingDefinition.createDefinition.HIERARCHY_ID({ ...definition, options });
            }),
        );
    }

    private loadHierarchyVariantSetting(
        definition: HierarchyVariantIdSettingDefinition,
        { settings, definitions }: LoadDefinitionProps,
    ): Observable<HierarchyVariantIdSettingDefinition> {
        const hierarchyId = settings.HIERARCHY_ID;

        if (definitions.HIERARCHY_ID) {
            const currentHierarchy = SettingDefinition.findOptionOfSelectSetting(
                definitions.HIERARCHY_ID,
                hierarchyId,
            );

            if (currentHierarchy && currentHierarchy.type === 'variant') {
                return this.masterData.getHierarchyVariants(currentHierarchy.dataSource).pipe(
                    map(hierarchies => {
                        const options = hierarchies.map(({ key, text }) => ({ key, label: text }));

                        return SettingDefinition.createDefinition.HIERARCHY_VARIANT_ID({
                            ...definition,
                            options,
                        });
                    }),
                );
            }
        }

        return of(SettingDefinition.createDefinition.HIERARCHY_VARIANT_ID({}));
    }

    private loadContextHierarchySetting(
        definition: LinkedHierarchiesSettingDefinition,
        { settings }: LoadDefinitionProps,
    ): Observable<LinkedHierarchiesSettingDefinition> {
        const internalKey = settings.INTERNAL_KEY;
        const hierarchyId = settings.HIERARCHY_ID;
        const nodes$ = hierarchyId
            ? this.masterData.getIntersectingHierarchies(hierarchyId)
            : internalKey
            ? this.masterData.getHierarchiesBasedOnInternalKey(internalKey)
            : of([]);

        return nodes$.pipe(
            map(hierarchies => {
                const options = hierarchies.map(hierarchy => ({
                    ...hierarchy,
                    key: hierarchy.hierarchyId,
                    label: hierarchy.hierarchyName,
                }));

                return SettingDefinition.createDefinition.LINKED_HIERARCHY_IDS({
                    ...definition,
                    options,
                });
            }),
        );
    }

    private loadSmartViewValues(
        definition: SmartViewValuesSettingDefinition,
        props: LoadDefinitionProps,
    ): Observable<SmartViewValuesSettingDefinition> {
        if (!(props.diff.SMART_VIEW || props.diff.SHORT_LABELS)) return of(definition);
        const smartView = props.diff.SMART_VIEW
            ? props.diff.SMART_VIEW.current
            : props.settings.SMART_VIEW;
        const useShortLabels = props.diff.SHORT_LABELS
            ? props.diff.SHORT_LABELS.current
            : props.settings.SHORT_LABELS;
        const checkedAggregations = getAggregationsFromSpec(props.specification);

        const options = checkedAggregations
            .find(aggregation => aggregation.smartViewTag === smartView)
            ?.items.map(item => ({
                key: item.key,
                label: useShortLabels ? item.shortLabel : `${item.label} [${item.key}]`,
            }));

        return of({
            ...definition,
            options: options || [],
        });
    }
}

/**
 * Means of optimizing deep comparisons. For large arrays (>1000 elements),
 * picks 100 elements and compares them instead. If picked values are equal,
 * assumes rest of them will also be.
 */
function fuzzyArrayDeepEqual<T>(itemA: T, itemB: T): boolean {
    return isEqualWith(itemA, itemB, (a, b) => {
        if (Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.length > 1000) {
            return range(0, 100).every(percent => {
                const index = Math.floor((percent * a.length) / 100);

                return fuzzyArrayDeepEqual(a[index], b[index]);
            });
        }

        return undefined;
    });
}
